import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tooltip", "image", "markersLayer"]
  static values = {
    zones: Array,
    markerIconUrl: String,
    numberFormat: { type: String, default: "european" }
  }

  connect() {
    this.currentZone = null
    this.markers = []

    // Wait for image to load before positioning markers
    if (this.hasImageTarget) {
      if (this.imageTarget.complete) {
        this.initializeMarkers()
      } else {
        this.imageTarget.addEventListener('load', () => this.initializeMarkers())
      }

      // Reposition on resize
      this.boundResize = this.repositionMarkers.bind(this)
      window.addEventListener('resize', this.boundResize)
    }
  }

  disconnect() {
    if (this.boundResize) {
      window.removeEventListener('resize', this.boundResize)
    }
  }

  initializeMarkers() {
    // Clear existing markers
    this.markers.forEach(m => m.remove())
    this.markers = []

    const layer = this.hasMarkersLayerTarget ? this.markersLayerTarget : this.element

    this.zonesValue.forEach(zone => {
      if (zone.map_x === null || zone.map_y === null) return

      const marker = document.createElement('div')
      marker.className = 'interactive-marker'
      marker.dataset.zoneId = zone.id
      marker.dataset.mapX = zone.map_x
      marker.dataset.mapY = zone.map_y
      marker.style.setProperty('--marker-color', zone.color || 'var(--primary)')

      if (this.markerIconUrlValue) {
        marker.innerHTML = `
          <div class="marker-pulse"></div>
          <img class="marker-icon" src="${this.markerIconUrlValue}" alt="">
        `
      } else {
        marker.innerHTML = `
          <div class="marker-pulse"></div>
          <div class="marker-dot"></div>
        `
      }

      marker.addEventListener('mouseenter', (e) => this.showTooltipForZone(e, zone.id))
      marker.addEventListener('mouseleave', () => this.hideTooltip())
      marker.addEventListener('mousemove', (e) => this.moveTooltip(e))

      layer.appendChild(marker)
      this.markers.push(marker)
    })

    this.repositionMarkers()
  }

  repositionMarkers() {
    if (!this.hasImageTarget) return

    const img = this.imageTarget
    const container = this.element

    // Get the actual rendered image dimensions and position
    // (accounting for object-fit: contain)
    const containerRect = container.getBoundingClientRect()
    const imgNaturalRatio = img.naturalWidth / img.naturalHeight
    const containerRatio = containerRect.width / containerRect.height

    let imgWidth, imgHeight, imgLeft, imgTop

    if (imgNaturalRatio > containerRatio) {
      // Image is wider - letterbox top/bottom
      imgWidth = containerRect.width
      imgHeight = containerRect.width / imgNaturalRatio
      imgLeft = 0
      imgTop = (containerRect.height - imgHeight) / 2
    } else {
      // Image is taller - letterbox left/right
      imgHeight = containerRect.height
      imgWidth = containerRect.height * imgNaturalRatio
      imgLeft = (containerRect.width - imgWidth) / 2
      imgTop = 0
    }

    // Position each marker relative to the actual image bounds
    this.markers.forEach(marker => {
      const mapX = parseFloat(marker.dataset.mapX)
      const mapY = parseFloat(marker.dataset.mapY)

      const pixelX = imgLeft + (mapX / 100) * imgWidth
      const pixelY = imgTop + (mapY / 100) * imgHeight

      marker.style.left = `${pixelX}px`
      marker.style.top = `${pixelY}px`
    })
  }

  showTooltipForZone(event, zoneId) {
    // Always get fresh data from zonesValue
    const zone = this.zonesValue.find(z => z.id === zoneId)
    if (zone) {
      this.showTooltip(event, zone)
    }
  }

  showTooltip(event, zone) {
    this.currentZone = zone
    if (!this.hasTooltipTarget) return

    const tooltip = this.tooltipTarget
    tooltip.innerHTML = `
      <div class="tooltip-header" style="border-color: ${zone.color || 'var(--primary)'}">
        <span class="tooltip-code" style="background: ${zone.color || 'var(--primary)'}">${zone.code || ''}</span>
        <span class="tooltip-name">${zone.name}</span>
      </div>
      <div class="tooltip-body">
        <div class="tooltip-stat">
          <span class="tooltip-value">${this.formatNumber(zone.liters)}</span>
          <span class="tooltip-label">litros</span>
        </div>
      </div>
    `

    tooltip.classList.add('visible')
    this.moveTooltip(event)
  }

  moveTooltip(event) {
    if (!this.hasTooltipTarget) return

    const tooltip = this.tooltipTarget
    const rect = this.element.getBoundingClientRect()

    let x = event.clientX - rect.left + 15
    let y = event.clientY - rect.top - 10

    // Keep tooltip within bounds
    const tooltipRect = tooltip.getBoundingClientRect()
    if (x + tooltipRect.width > rect.width) {
      x = event.clientX - rect.left - tooltipRect.width - 15
    }
    if (y + tooltipRect.height > rect.height) {
      y = rect.height - tooltipRect.height - 10
    }
    if (y < 10) y = 10

    tooltip.style.left = `${x}px`
    tooltip.style.top = `${y}px`
  }

  hideTooltip() {
    this.currentZone = null
    if (this.hasTooltipTarget) {
      this.tooltipTarget.classList.remove('visible')
    }
  }

  formatNumber(num) {
    if (this.numberFormatValue === "international") {
      return Math.round(num).toLocaleString('en-US')
    } else {
      return Math.round(num).toLocaleString('de-DE')
    }
  }

  // Update zone data (called from dashboard controller)
  updateZones(zones) {
    this.zonesValue = zones
    // Update tooltip content if currently showing (keeps position, updates value)
    if (this.currentZone && this.hasTooltipTarget && this.tooltipTarget.classList.contains('visible')) {
      const updatedZone = zones.find(z => z.id === this.currentZone.id)
      if (updatedZone) {
        this.currentZone = updatedZone
        // Just update the value, not the whole tooltip
        const valueEl = this.tooltipTarget.querySelector('.tooltip-value')
        if (valueEl) {
          valueEl.textContent = this.formatNumber(updatedZone.liters)
        }
      }
    }
  }
}
